<script lang="ts">
  import { vuAudio } from '$lib/audio/vuAudio.svelte';
  import { savePrefs } from '$lib/audio/audioPreferences';
  import { playBondSound } from '$lib/audio/sounds';

  /**
   * Mute toggle + volume slider for the bond ceremony sound.
   *
   * Every interaction here is a user gesture, so it's safe to call
   * `vuAudio.init()` lazily — that lets the user preview the sound
   * without first having to trigger a real bond.
   */

  async function ensureReady(): Promise<void> {
    if (!vuAudio.ready) await vuAudio.init();
  }

  async function toggleMute() {
    await ensureReady();
    vuAudio.toggleMute();
    savePrefs();
  }

  async function setVolume(e: Event) {
    await ensureReady();
    const target = e.target as HTMLInputElement;
    vuAudio.setVolume(parseFloat(target.value));
    savePrefs();
  }

  /** Play the actual bond sound so the user can hear their setting. */
  async function preview() {
    await ensureReady();
    if (vuAudio.muted) return;
    playBondSound();
  }
</script>

<div class="audio-toggle">
  <button
    class="at-mute"
    type="button"
    onclick={toggleMute}
    aria-label={vuAudio.muted ? 'Unmute bond sound' : 'Mute bond sound'}
    aria-pressed={vuAudio.muted}
  >
    {#if vuAudio.muted}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M11 5L6 9H2v6h4l5 4V5z" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M23 9l-6 6M17 9l6 6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    {:else}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M11 5L6 9H2v6h4l5 4V5z" stroke-linecap="round" stroke-linejoin="round" />
        <path
          d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    {/if}
  </button>

  <input
    class="at-slider"
    type="range"
    min="0"
    max="1"
    step="0.05"
    value={vuAudio.volume}
    oninput={setVolume}
    disabled={vuAudio.muted}
    aria-label="Bond sound volume"
  />

  <button
    class="at-preview"
    type="button"
    onclick={preview}
    disabled={vuAudio.muted}
    aria-label="Preview bond sound"
  >
    Preview
  </button>
</div>

<style>
  .audio-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  .at-mute {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: var(--transition);
  }
  :global(body.brutal) .at-mute {
    border-width: 2px;
  }
  .at-mute:hover {
    background: var(--surface-2);
    border-color: var(--cyan);
  }
  .at-mute svg {
    width: 16px;
    height: 16px;
  }

  .at-slider {
    flex: 1 1 auto;
    min-width: 0;
    accent-color: var(--cyan);
    cursor: pointer;
  }
  .at-slider:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .at-preview {
    flex-shrink: 0;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text);
    padding: 6px 10px;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: var(--transition);
  }
  :global(body.brutal) .at-preview {
    border-width: 2px;
  }
  .at-preview:hover:not(:disabled) {
    background: var(--surface-2);
    border-color: var(--cyan);
    color: var(--cyan);
  }
  .at-preview:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
